<script lang="ts">
  export interface CreateSupportSessionFormProps {
    supportSession?: Partial<CreateNewSupportSessionRequest>
  }
</script>

<script setup lang="ts">
  import { computed, onMounted } from 'vue'
  import {
    accessCodes,
    type ISODateTimeString,
    type CreateNewSupportSessionRequest,
    type SupportPerson
  } from '@gat-ship/types'
  import { testIds } from '@gat-ship/types'
  import { useSupportPersonnel } from '../../composables/useSupportPersonnel'
  import { Temporal } from '@js-temporal/polyfill'
  import {
    Alert,
    AlertDescription,
    Avatar,
    AvatarFallback,
    AvatarImage,
    InputGroupCombobox,
    InputGroupDatePicker,
    FormField,
    ModalSectionHeader,
    ModalSectionHeaderTitle,
    InputGroupTextarea,
    RadioCardGroup,
    RadioCardGroupItem,
    type SelectInputItem,
    useAppLoading,
    useForm,
    useFormSubmit,
    z,
    ModalSection
  } from '@gat-ship/vue-components'
  import { FieldGroup } from '@gat-ship/vue-components'
  import { useUser } from '@gat-ship/user'
  import { globalUtils } from '@gat-ship/utils'
  import { useSupportSessionContext } from '../../composables/useSupportSession'
  import { IconLocked, IconUnlocked } from '@gat-ship/icons'

  const props = defineProps<CreateSupportSessionFormProps>()
  const emits = defineEmits<{ (e: 'saved'): void }>()

  const user = useUser()
  const { supportPersonnel, fetchSupportPersonnel } = useSupportPersonnel()
  const { commands } = useSupportSessionContext()!
  const { setLoadingId, removeLoadingId } = useAppLoading()

  // Schema is the single source of truth for shape + validation,
  // including the grantFrom < grantTo cross-field rule (superRefine).
  const supportPersonnelSchema = z
    .object({
      sessionId: z.string().min(1),
      supportUserId: z.string().min(1, 'Support Person is required'),
      grantFrom: z.string().min(1, 'Grant From is required'),
      grantTo: z.string().min(1, 'Grant To is required'),
      grantReason: z.string(),
      // String enum keeps us on the FormRadioGroup surface (which is
      // string-typed); mapped to `grantTenantAdmin: boolean` at API-
      // payload time.
      grantTenantAdminMode: z.enum(['my', 'admin'])
    })
    .superRefine((data, ctx) => {
      if (!data.grantFrom || !data.grantTo) return
      try {
        const from = Temporal.Instant.from(data.grantFrom)
        const to = Temporal.Instant.from(data.grantTo)
        if (Temporal.Instant.compare(to, from) < 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['grantTo'],
            message: 'Grant To must not be before Grant From'
          })
        }
      } catch {
        // If either string isn't a parseable Instant the .min(1) check
        // above has already flagged the empty case; anything else is a
        // malformed ISO string the picker won't produce in practice.
      }
    })

  type SupportSessionFormValues = z.infer<typeof supportPersonnelSchema>

  const form = useForm({
    defaultValues: {
      sessionId: globalUtils.functions.uuid(),
      supportUserId: '',
      grantFrom: globalUtils.functions.fromNow() as string,
      grantTo: globalUtils.functions.fromNow({ days: 1 }) as string,
      grantReason: '',
      grantTenantAdminMode: 'my'
    } as SupportSessionFormValues,
    validators: {
      onSubmit: supportPersonnelSchema
    },
    // Validation has already passed by the time this runs (the schema in
    // `validators` is the gate), so `value` is trusted form input. The only
    // job here is the side effect: transform to the API payload and hand it
    // to the collection command, which fires the request and reconciles the
    // list from the server. Any thrown error is captured by `useFormSubmit`
    // into `errorMessage` and shown below.
    onSubmit: async ({ value }) => {
      const payload = toCreateSupportSessionRequest(value)
      try {
        setLoadingId(payload.sessionId)
        await commands.createSupportSession(
          payload,
          globalUtils.functions.uuid()
        )
        // Close only on success, so a failed request keeps the modal open
        // with the error visible.
        emits('saved')
      } finally {
        removeLoadingId(payload.sessionId)
      }
    }
  })

  const { submit, errorMessage, isSubmitting } = useFormSubmit(form)

  const grantFrom = form.useStore((s) => s.values.grantFrom)
  const supportUserId = form.useStore((s) => s.values.supportUserId)

  const selectedSupportPerson = computed(() =>
    supportPersonnel.value.find((p) => p.userId === supportUserId.value)
  )

  // The picker and the `selectedSupportPerson` lookup above share this
  // component's single `useSupportPersonnel()` instance and its one
  // fetched list. Lazy fetch self-guards against refetching.
  fetchSupportPersonnel()

  const supportPersonnelItems = computed<SelectInputItem<SupportPerson>[]>(() =>
    supportPersonnel.value.map((p) => ({
      value: p.userId,
      title: p.fullName,
      searchValue: p.fullName,
      metaData: p
    }))
  )

  const isPersonnelLoading = computed(() => supportPersonnel.value.length === 0)

  // grantFrom can't be earlier than today.
  const grantFromDisabledRanges = computed(() => [
    {
      from: 'epoch' as const,
      to: Temporal.Now.plainDateISO().subtract({ days: 1 })
    }
  ])

  // grantTo can't be earlier than grantFrom.
  const grantToDisabledRanges = computed(() => {
    if (!grantFrom.value) return []
    try {
      return [
        {
          from: 'epoch' as const,
          to: Temporal.Instant.from(grantFrom.value)
            .toZonedDateTimeISO(
              Intl.DateTimeFormat().resolvedOptions().timeZone
            )
            .subtract({ days: 1 })
            .toPlainDate()
        }
      ]
    } catch {
      return []
    }
  })

  /**
   * Pure boundary transform: validated form values → API request DTO. The
   * only shape change is the `'my' | 'admin'` UI enum collapsing to the
   * `grantTenantAdmin` boolean the backend expects. No store lookups, no
   * enrichment — the server response is the source of truth for everything
   * else (full name, tenant info), reconciled via the post-submit refetch.
   */
  function toCreateSupportSessionRequest(
    value: SupportSessionFormValues
  ): CreateNewSupportSessionRequest {
    return {
      sessionId: value.sessionId,
      supportUserId: value.supportUserId,
      grantFrom: value.grantFrom as ISODateTimeString,
      grantTo: value.grantTo as ISODateTimeString,
      grantReason: value.grantReason ?? undefined,
      grantTenantAdmin: value.grantTenantAdminMode === 'admin'
    }
  }

  function setRequestPayload(payload: Partial<CreateNewSupportSessionRequest>) {
    form.reset({
      sessionId: payload.sessionId ?? globalUtils.functions.uuid(),
      supportUserId: payload.supportUserId ?? '',
      grantFrom: (payload.grantFrom ??
        globalUtils.functions.fromNow()) as string,
      grantTo: (payload.grantTo ??
        globalUtils.functions.fromNow({ days: 1 })) as string,
      grantReason: payload.grantReason ?? '',
      grantTenantAdminMode: payload.grantTenantAdmin ? 'admin' : 'my'
    })
  }

  // Seed form values from the supportSession prop at mount.
  onMounted(() => {
    if (props.supportSession) {
      setRequestPayload(props.supportSession)
    }
  })

  defineExpose({
    setRequestPayload,
    submit,
    errorMessage,
    isSubmitting
  })
</script>

<template>
  <ModalSection class="pb-5">
    <FormField
      :form="form"
      name="supportUserId"
      label="Support Person"
      hint="If you have been in contact with someone from support, please select them here."
      required="Support Person is required"
    >
      <template #default="{ field, invalid }">
        <InputGroupCombobox
          :id="field.name"
          :items="supportPersonnelItems"
          :model-value="field.state.value"
          :loading="isPersonnelLoading"
          :aria-invalid="invalid"
          placeholder="Select support person"
          :data-testid="
            testIds.support.modal.createSession.supportPersonnelInput
          "
          @update:model-value="field.handleChange"
          @blur="field.handleBlur"
        />
      </template>
    </FormField>
  </ModalSection>
  <ModalSection class="pb-5">
    <ModalSectionHeader>
      <ModalSectionHeaderTitle
        >Support Person Privileges</ModalSectionHeaderTitle
      >
    </ModalSectionHeader>
    <FormField
      v-if="
        user.hasPermission(
          accessCodes.system.support
            .createSupportSessionWithTenantAdminPrivileges
        )
      "
      :form="form"
      name="grantTenantAdminMode"
      :input-group="false"
    >
      <template #default="{ field }">
        <RadioCardGroup
          :model-value="field.state.value"
          @update:model-value="field.handleChange"
        >
          <RadioCardGroupItem
            value="my"
            :icon="IconLocked"
            label="My Priveleges"
            :description="`Will grant ${selectedSupportPerson ? selectedSupportPerson.fullName : 'the support person'} access to your tenant with your privileges.`"
          >
            <template #icon>
              <Avatar class="size-full">
                <AvatarImage
                  v-if="user.userProfile.value?.profileImageUrl"
                  :src="user.userProfile.value.profileImageUrl"
                />
                <AvatarFallback>
                  <IconLocked class="size-5 shrink-0" />
                </AvatarFallback>
              </Avatar>
            </template>
          </RadioCardGroupItem>
          <RadioCardGroupItem
            value="admin"
            :icon="IconUnlocked"
            label="Tenant Admin Privileges"
            :description="`Will grant ${selectedSupportPerson ? selectedSupportPerson.fullName : 'the support person'} full access to your tenant.`"
          />
        </RadioCardGroup>
      </template>
    </FormField>
  </ModalSection>

  <ModalSection class="pb-5">
    <ModalSectionHeader>
      <ModalSectionHeaderTitle>Support Duration</ModalSectionHeaderTitle>
    </ModalSectionHeader>
    <FieldGroup>
      <FormField
        :form="form"
        name="grantFrom"
        label="Grant From"
        required="Grant From is required"
      >
        <template #default="{ field, invalid }">
          <InputGroupDatePicker
            :id="field.name"
            :model-value="field.state.value || undefined"
            anchor-time="midnight"
            :aria-invalid="invalid"
            :disabled-date-ranges="grantFromDisabledRanges"
            data-testid="add-support-session-input-grant-from"
            @update:model-value="(v) => field.handleChange(v ?? '')"
            @blur="field.handleBlur"
          />
        </template>
      </FormField>
      <FormField
        :form="form"
        name="grantTo"
        label="Grant To"
        required="Grant To is required"
      >
        <template #default="{ field, invalid }">
          <InputGroupDatePicker
            :id="field.name"
            :model-value="field.state.value || undefined"
            anchor-time="end-of-day"
            :aria-invalid="invalid"
            :disabled-date-ranges="grantToDisabledRanges"
            data-testid="add-support-session-input-grant-to"
            @update:model-value="(v) => field.handleChange(v ?? '')"
            @blur="field.handleBlur"
          />
        </template>
      </FormField>
    </FieldGroup>
  </ModalSection>

  <ModalSection>
    <ModalSectionHeader>
      <ModalSectionHeaderTitle>Grant Details</ModalSectionHeaderTitle>
    </ModalSectionHeader>
    <FieldGroup>
      <FormField :form="form" name="grantReason" label="Grant Reason">
        <template #default="{ field }">
          <InputGroupTextarea
            :id="field.name"
            :model-value="field.state.value ?? ''"
            data-testid="add-support-session-input-grant-reason"
            @update:model-value="field.handleChange"
            @blur="field.handleBlur"
          />
        </template>
      </FormField>
    </FieldGroup>
  </ModalSection>

  <ModalSection v-if="errorMessage">
    <Alert variant="destructive" data-testid="add-support-session-error">
      <AlertDescription>{{ errorMessage }}</AlertDescription>
    </Alert>
  </ModalSection>
</template>
